import { useRef } from 'react';

interface SelectionValues {
  start: number;
  end: number;
  value: string;
  beforeText: string;
  afterText: string;
}

/**
 * Keep input cursor in the correct position if possible.
 * Is this necessary since we have `formatter` which may mass the content?
 *
 * @param input - Input element
 * @param focused - Whether input is focused
 */
export function useCursor(
  input: HTMLInputElement | null,
  focused: boolean,
): [() => void, () => void] {
  const selectionRef = useRef<SelectionValues | null>(null);

  if (!input) {
    return [() => {}, () => {}];
  }

  // Record position
  const recordCursor = () => {
    try {
      const selectionStart = input.selectionStart ?? input.value.length;
      const selectionEnd = input.selectionEnd ?? input.value.length;
      const beforeText = input.value.slice(0, selectionStart);
      const afterText = input.value.slice(selectionEnd);

      selectionRef.current = {
        start: selectionStart,
        end: selectionEnd,
        value: input.value,
        beforeText,
        afterText,
      };
    } catch {
      console.warn(
        `[useCursor] - Failed to read the 'selectionStart' property from 'HTMLInputElement'`,
      );
    }
  };

  /**
   * Restore logic:
   *
   *  1. back string same
   *  2. start string same
   */
  const restoreCursor = () => {
    const selection = selectionRef.current;

    if (input && selection && focused) {
      try {
        let startPosition = input.value.length;
        const { start, beforeText, afterText } = selection;

        if (input.value.endsWith(afterText)) {
          startPosition = input.value.length - selection.afterText.length;
        } else if (input.value.startsWith(beforeText)) {
          startPosition = beforeText.length;
        } else {
          const beforeLastChar = beforeText[start - 1];
          if (beforeLastChar) {
            const newIndex = input.value.indexOf(beforeLastChar, start - 1);
            if (newIndex !== -1) {
              startPosition = newIndex + 1;
            }
          }
        }
        input.setSelectionRange(startPosition, startPosition);
      } catch (error) {
        console.warn(
          `[useCursor] - Something wrong with cursor restore: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        );
      }
    }
  };

  return [recordCursor, restoreCursor];
}
